import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { HandlerWithResult, makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { replaceInChain } from "@insite/client-framework/HandlerCreator";
import { getUrl } from "@insite/client-framework/ServerSideRendering";
import { Session } from "@insite/client-framework/Services/SessionService";
import { getSelectedProductPath, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBrandStateById } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrand from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrand";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import { chain, PrepareStructuredPageData } from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/GenerateStructuredPageData";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { generateLinksFrom } from "@tLinc/Overrides/Components/PageBreadcrumbs";

type HandlerType = HandlerWithResult<
    {
        product: ProductModel;
        productInfo?: ProductInfo;
        session: Session;
        brandId?: string | null;
        categoryIdWithBrandId?: string | null;
        categoryId?: string | null;
        metaDescription?: string | null;
    },
    { structuredPageData: string }
>;

export const CustomProductSchemaStructuredData: HandlerType = async props => {
    const {
        id,
        productTitle,
        smallImagePath,
        mediumImagePath,
        largeImagePath,
        canonicalUrl,
        manufacturerItem,
        detail,
        brand: productBrand,
    } = props.parameter.product;
    const state = props.getState();
    const breadcrumbLinks = state.components.breadcrumbs.links;
    const linksState = state.links;
    const nodeId = getCurrentPage(state).nodeId;
    const homePageUrl = getHomePageUrl(state);
    const links = breadcrumbLinks || generateLinksFrom(linksState, nodeId, homePageUrl);
    const url: { protocol: string; host: string } = IS_SERVER_SIDE ? getUrl()! : window.location;
    const authority = `${url.protocol}//${url.host}`;
    const location = getLocation(state);
    let productPath =
        getSelectedProductPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    let queryString = location.search;
    if (location.pathname.toLowerCase().startsWith("/content/page/") && productPath.indexOf("?") > -1) {
        queryString = productPath ? productPath.substring(productPath.indexOf("?")) : "";
        productPath = productPath.substring(0, productPath.indexOf("?"));
    }
    const catalogPageState = getCatalogPageStateByPath(state, productPath);
    const metaDescription = catalogPageState?.value?.metaDescription;
    const productNumber = catalogPageState?.value?.productName;

    const data = {
        "@context": "https://schema.org",
        "@type": "Product",
        productID: id,
        name: productTitle,
        image: [smallImagePath, mediumImagePath, largeImagePath],
        mpn: manufacturerItem,
        sku: detail?.sku || productNumber,
        url: canonicalUrl,
        description: metaDescription || 'Product Description',
    } as any;

    const productInfo = props.parameter.productInfo;
    if (
        productInfo &&
        !props.parameter.product.quoteRequired &&
        (!getIsWebCrawler() || getSettingsCollection(state).websiteSettings.includeRealTimeInventoryAndPricingForSeo)
    ) {
        await waitFor(() => !!productInfo.pricing && !!productInfo.inventory);

        const { pricing, inventory, unitOfMeasure } = productInfo;

        if (pricing && inventory && pricing.unitNetPrice > 0) {
            const availability = inventory?.inventoryAvailabilityDtos?.find(
                o => o.unitOfMeasure.toLowerCase() === (unitOfMeasure?.toLowerCase() || ""),
            )?.availability;

            data.offers = {
                "@type": "Offer",
                availability:
                    availability?.messageType !== 2
                        ? availability?.messageType === 3
                            ? "https://schema.org/LimitedAvailability"
                            : "https://schema.org/InStock"
                        : "https://schema.org/OutOfStock",
                price: pricing.unitNetPrice,
                priceCurrency: props.parameter.session?.currency?.currencyCode,
                url: canonicalUrl,
            };
        }
    }

    if (!data.offers) {
        props.result = { structuredPageData: "" };
        return;
    }

    if (props.parameter.brandId) {
        let brandState = getBrandStateById(state, props.parameter.brandId);
        let brand = productBrand || brandState.value;
        if (!brand && !brandState.isLoading) {
            const awaitableLoadBrand = makeHandlerChainAwaitable(loadBrand);
            await awaitableLoadBrand({ brandId: props.parameter.brandId })(props.dispatch, props.getState);
            brandState = getBrandStateById(props.getState(), props.parameter.brandId);
            brand = brandState.value;
        }

        if (brand) {
            data.brand = {
                "@type": "Brand",
                identifier: brand.id,
                name: brand.name,
                url: brand.detailPagePath,
            };
        }
    }

    if (props.parameter.categoryIdWithBrandId || props.parameter.categoryId) {
        const category = getCategoryState(
            state,
            props.parameter.categoryIdWithBrandId || props.parameter.categoryId || "",
        ).value;

        if (category) {
            data.category = category.path;
        }
    }

    // BreadcrumbList handling
    const breadcrumbData = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: links.map((o, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: o.children,
            item: authority + o.href,
        })),
    } as any;

    if (links.length) {
        delete breadcrumbData["itemListElement"][links.length - 1]["item"];
    }

    props.result = {
        structuredPageData: JSON.stringify([
            data,
            breadcrumbData
        ])
    };
};

replaceInChain(chain, PrepareStructuredPageData, CustomProductSchemaStructuredData);